/**
 * Zustand Auth Store
 * Global authentication state management
 *
 * @module stores/auth-store
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { signOut, type User } from 'firebase/auth';
import type { Household } from '@sagamoon/shared/types';
import { auth } from '../lib/firebase';

/**
 * Authentication state interface
 */
interface AuthState {
  // ========================================
  // State
  // ========================================

  /**
   * Firebase Auth user object
   * Null when user is not authenticated
   */
  user: User | null;

  /**
   * Household data from Firestore
   * Contains children profiles, preferences, etc.
   * Null when not loaded or user not authenticated
   */
  household: Household | null;

  /**
   * Authentication status
   * True when user has valid Firebase Auth session
   */
  isAuthenticated: boolean;

  /**
   * Loading state for auth operations
   * True during initial auth check, login, signup, etc.
   */
  isLoading: boolean;

  /**
   * Error message from auth operations
   * Null when no error
   */
  error: string | null;

  // ========================================
  // Actions
  // ========================================

  /**
   * Set authenticated user
   * Called after successful login/signup
   *
   * @param user - Firebase Auth user object
   */
  setUser: (user: User | null) => void;

  /**
   * Set household data
   * Called after fetching household from Firestore
   *
   * @param household - Household data
   */
  setHousehold: (household: Household | null) => void;

  /**
   * Set loading state
   * Used during async auth operations
   *
   * @param isLoading - Loading state
   */
  setLoading: (isLoading: boolean) => void;

  /**
   * Set error message
   * Called when auth operation fails
   *
   * @param error - Error message in Finnish
   */
  setError: (error: string | null) => void;

  /**
   * Clear all auth state
   * Called on logout or auth error
   */
  clearAuth: () => void;

  /**
   * Update specific household fields
   * Used for partial updates (e.g., add child profile)
   *
   * @param updates - Partial household data to merge
   */
  updateHousehold: (updates: Partial<Household>) => void;

  /**
   * Logout user
   * Calls Firebase signOut() and clears all auth state
   * Note: Query cache clearing is handled by useLogout hook
   */
  logout: () => Promise<void>;
}

/**
 * Zustand auth store with persistence
 *
 * SECURITY NOTE:
 * - Only persists minimal non-sensitive data
 * - User/household objects are NOT persisted (security risk)
 * - Firebase Auth handles token persistence automatically
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      // ========================================
      // Initial State
      // ========================================
      user: null,
      household: null,
      isAuthenticated: false,
      isLoading: true, // Start as loading (auth check on app init)
      error: null,

      // ========================================
      // Actions
      // ========================================

      setUser: (user) => {
        set({
          user,
          isAuthenticated: !!user,
          error: null,
        });
      },

      setHousehold: (household) => {
        set({
          household,
          error: null,
        });
      },

      setLoading: (isLoading) => {
        set({ isLoading });
      },

      setError: (error) => {
        set({ error });
      },

      clearAuth: () => {
        set({
          user: null,
          household: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      },

      updateHousehold: (updates) => {
        set((state) => ({
          household: state.household ? { ...state.household, ...updates } : null,
        }));
      },

      logout: async () => {
        try {
          await signOut(auth);
          set({
            user: null,
            household: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          console.error('Logout error:', error);
          // Still clear local state even if signOut fails
          set({
            user: null,
            household: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      },
    }),
    {
      name: 'sagamoon-auth-storage',

      /**
       * Partialize: Only persist non-sensitive data
       * SECURITY: Never persist user/household data in localStorage
       * Firebase Auth SDK handles secure token storage
       */
      partialize: (state) => ({
        // Only persist authentication status for faster app initialization
        // User/household are re-fetched from Firebase on app load
        isAuthenticated: state.isAuthenticated,
      }),

      /**
       * Version for migration support
       * Increment when auth store structure changes
       */
      version: 1,

      /**
       * Migration function for future store structure changes
       */
      migrate: (persistedState: unknown, version: number) => {
        if (version === 0) {
          // Migration from v0 to v1 (if needed in future)
          return persistedState as AuthState;
        }
        return persistedState as AuthState;
      },
    }
  )
);

/**
 * Selector hooks for common auth queries
 * Use these instead of accessing store directly for better performance
 */

/**
 * Check if user is authenticated
 * @returns True if user has valid session
 */
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);

/**
 * Get current user object
 * @returns Firebase Auth user or null
 */
export const useCurrentUser = () => useAuthStore((state) => state.user);

/**
 * Get current household data
 * @returns Household data or null
 */
export const useHousehold = () => useAuthStore((state) => state.household);

/**
 * Get auth loading state
 * @returns True during auth operations
 */
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);

/**
 * Get auth error message
 * @returns Finnish error message or null
 */
export const useAuthError = () => useAuthStore((state) => state.error);
